import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import Pre from "@ui/pre/Pre";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import Signal from "badmfck-signal";
import { CircleCheck, Gauge, Landmark, Link2, Plus, Receipt, RefreshCw, SlidersHorizontal, Store, TriangleAlert, Unlink } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import styled from "styled-components";
import { GD } from "src/GD";
import { notify } from "src/tools/notify";
import type { TFeesType } from "../Fees";
import { FormParser } from "../FormParser";
import { AdminButton } from "../pages/administration/AdminControls";
import { integrationHost, integrationMethodType, S_INTEGRATION_EDIT_POPUP, S_INTEGRATION_SELECT_POPUP } from "../pages/IntegrationsList";
import { S_CONFIRM } from "../popup/general/ConfirmDialog";
import { ModernFees } from "../providers/ModernFees";
import { channelStatus, feeStates, formatDate, formatIban, matchesQuery, methodTypeLabel, plural, toDate } from "../providers/providersData";
import {
	Card,
	CardBrandIcons,
	Chip,
	CopyButton,
	DialogBody,
	DialogTabs,
	EmptyState,
	embeddedFormSx,
	Facts,
	IconTile,
	Item,
	ItemList,
	MethodTile,
	Mono,
	Muted,
	Notice,
	Pill,
	ProviderDialog,
	RowChevron,
	SearchField,
	Section,
	SkeletonRows,
	StateDot,
	StatusPill,
	WarningText,
} from "../providers/providersUi";
import { maskSecrets, ValueTree } from "../providers/ValueTree";
import {
	channelFeesTarget,
	editMerchantChannel,
	getBeneficiaryLinkData,
	getChannelBeneficiaries,
	getChannelSignature,
	isOpenBankingChannel,
	mergePaymentChannel,
	normalizeBeneficiary,
	refreshPaymentChannel,
	S_CHANNEL_ITEM_POPUP,
	S_CHANNELS_LIST_REQUEST_UPDATE,
	type TBeneficiary,
	type TPaymentChannel,
} from "./channelData";

enum TAB {
	OVERVIEW = 0,
	RATES = 1,
	LIMITS = 2,
	SETTINGS = 3,
	BENEFICIARY = 4,
	ACTUAL = 5,
}

const FEE_TABS: { type: TFeesType; tab: TAB; title: string; icon: ReactNode }[] = [
	{ type: "rates", tab: TAB.RATES, get title() { return msg("rates"); }, icon: <Receipt size={16} /> },
	{ type: "limits", tab: TAB.LIMITS, get title() { return msg("limits"); }, icon: <Gauge size={16} /> },
	{ type: "settings", tab: TAB.SETTINGS, get title() { return msg("settings"); }, icon: <SlidersHorizontal size={16} /> },
];

const popupKey = (channel: TPaymentChannel) => (channel.linked ? `linked:${channel.linked.id}` : `channel:${channel.id}`);

export const ChannelItemContentPopup = () => {
	useLocale();
	const channel = S_CHANNEL_ITEM_POPUP.use();
	if (!channel) return null;
	return <ChannelDetails key={popupKey(channel)} channel={channel} />;
};

const ChannelDetails = ({ channel }: { channel: TPaymentChannel }) => {
	useLocale();
	const [tab, setTab] = useState<number>(TAB.OVERVIEW);
	const close = () => S_CHANNEL_ITEM_POPUP.invoke(null);
	const status = channelStatus(channel);
	const openBanking = isOpenBankingChannel(channel);
	const linked = !!channel.linked;
	const label = channel.name + (channel.linked?.alias ? ` · ${channel.linked.alias}` : "");

	const latest = useRef(channel);
	latest.current = channel;
	const [revision, setRevision] = useState(0);
	S_CHANNELS_LIST_REQUEST_UPDATE.use([], () => setRevision(value => value + 1));

	// After any change elsewhere, a fresher copy replaces the shown channel while the dialog stays mounted.
	useEffect(() => {
		if (!revision) return;
		let active = true;
		void refreshPaymentChannel(latest.current).then(fresh => {
			if (!active || !fresh) return;
			const next = mergePaymentChannel(latest.current, fresh);
			if (getChannelSignature(next) !== getChannelSignature(latest.current)) S_CHANNEL_ITEM_POPUP.invoke(next);
		});
		return () => {
			active = false;
		};
	}, [revision]);

	return (
		<ProviderDialog
			width={980}
			fullHeight
			eyebrow={linked ? <>{msg("merchantChannelMerchant")}{channel.linked.merchant_id}</> : <>{msg("paymentChannel")}{channel.provider?.name ? ` · ${channel.provider.name}` : ""}</>}
			title={channel.name || msg("templateChannel_e25c3617", { v0: channel.id })}
			titleAction={channel.linked?.alias ? <Chip title={msg("merchantAlias")}>{channel.linked.alias}</Chip> : undefined}
			meta={
				<>
					<StateDot status={status} />
					<span>
						<MethodInline channel={channel} />
					</span>
					{channel.currency && <Chip>{channel.currency}</Chip>}
					<CardBrandIcons instrument={channel.instrument_type} />
					<span className="push">
						{linked ? msg("templateLinkChannel_3bdb5cd7", { v0: channel.linked.id, v1: channel.id }) : msg("templateChannel_e25c3617", { v0: channel.id })}
					</span>
				</>
			}
			onClose={close}>
			<DialogTabs>
				<Tabs variant="modern" selected={tab} onSectionSelect={setTab}>
					<TabItem title={msg("overview")} icon={<CircleCheck size={16} />}>
						<DialogBody>
							<ChannelOverview channel={channel} onOpenTab={setTab} />
						</DialogBody>
					</TabItem>
					<TabItem title={msg("rates")} icon={FEE_TABS[0].icon}>
						<DialogBody>
							<ModernFees key="rates" {...channelFeesTarget(channel)} type="rates" label={label} />
						</DialogBody>
					</TabItem>
					<TabItem title={msg("limits")} icon={FEE_TABS[1].icon}>
						<DialogBody>
							<ModernFees key="limits" {...channelFeesTarget(channel)} type="limits" label={label} />
						</DialogBody>
					</TabItem>
					<TabItem title={msg("settings")} icon={FEE_TABS[2].icon}>
						<DialogBody>
							<ModernFees key="settings" {...channelFeesTarget(channel)} type="settings" label={label} />
						</DialogBody>
					</TabItem>
					{openBanking && (
						<TabItem title={msg("beneficiary")} icon={<Landmark size={16} />}>
							<DialogBody>
								<ChannelBeneficiarySetup channel={channel} />
							</DialogBody>
						</TabItem>
					)}
					{linked && (
						<TabItem title={msg("actualFees")} icon={<Store size={16} />}>
							<DialogBody>
								<ActualFeesView data={channel.actual_fees} />
							</DialogBody>
						</TabItem>
					)}
				</Tabs>
			</DialogTabs>
		</ProviderDialog>
	);
};

const MethodInline = ({ channel }: { channel: TPaymentChannel }) => { useLocale(); return ((
	<span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
		<MethodTile type={channel.terminal?.type} small />
		{channel.terminal?.name ?? channel.terminal_name ?? methodTypeLabel(channel.terminal?.type)}
	</span>
)); };

/* ---------- Overview ---------- */

type TFeesSummary = Partial<Record<TFeesType, { count: number; current?: string; scheduled: number; error?: string }>>;

const useFeesSummary = (channel: TPaymentChannel) => {
	useLocale();
	const [summary, setSummary] = useState<TFeesSummary | null>(null);
	const target = channelFeesTarget(channel);
	useEffect(() => {
		let active = true;
		void Promise.all(
			FEE_TABS.map(async ({ type }) => {
				const response = await GD.REQ_API.request({ method: "fees/get", data: { ...target, type } });
				if (response.error) return [type, { count: 0, scheduled: 0, error: response.error.message }] as const;
				const fees = Array.isArray(response.data) ? response.data : [];
				const states = feeStates(fees);
				const current = fees[states.indexOf("current")];
				return [type, { count: fees.length, current: current ? (formatDate(current.activated_from) ?? undefined) : undefined, scheduled: states.filter(state => state === "scheduled").length }] as const;
			}),
		).then(entries => {
			if (active) setSummary(Object.fromEntries(entries));
		});
		return () => {
			active = false;
		};
	}, [target.target_id, target.target_type]);
	return summary;
};

const SetupBox = styled.div`
	.check {
		display: grid;
		place-items: center;
		flex: 0 0 28px;
		width: 28px;
		height: 28px;
		border-radius: 50%;
	}
	.check.ok {
		background: #ecfdf3;
		color: #067647;
	}
	.check.missing {
		background: #fffaeb;
		color: #b54708;
	}
	.check.pending {
		background: #f2f4f7;
		color: #98a2b3;
	}
	.item-title {
		color: #182230;
		font-weight: 600;
	}
	.item-sub {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 2px 6px;
		color: #667085;
		font-size: 12px;
	}
	.item-sub.missing {
		color: #b54708;
	}
	.go {
		color: #667085;
		font-size: 12px;
		font-weight: 500;
		white-space: nowrap;
	}
	.go.setup {
		color: #175cd3;
	}
`;

type TCheck = { key: string; title: string; ok: boolean | null; text: ReactNode; tab?: TAB };

const CheckMark = ({ ok }: { ok: boolean | null }) => { useLocale(); return ((
	<span className={msg("templateCheck_c4a7e94e", { v0: ok === null ? msg("checkingPending") : ok ? msg("checkingOk") : msg("checkingMissing") })} aria-hidden="true">
		{ok === false ? <TriangleAlert size={14} /> : <CircleCheck size={15} />}
	</span>
)); };

/**
 * What the channel needs before it can process payments, each item linked to the place where it is set up.
 * The integration is managed right here; fee configurations open their tabs.
 */
const ChannelOverview = ({ channel, onOpenTab }: { channel: TPaymentChannel; onOpenTab: (tab: number) => void }) => {
	useLocale();
	const fees = useFeesSummary(channel);
	const beneficiaries = getChannelBeneficiaries(channel);
	const openBanking = isOpenBankingChannel(channel);
	const checks: TCheck[] = [
		...FEE_TABS.map(({ type, tab, title }) => {
			const item = fees?.[type];
			return {
				key: type,
				title,
				tab,
				ok: !fees ? null : !!item?.current,
				text: !fees
					? "Checking…"
					: item?.error
						? item.error
						: item?.current
							? msg("templateInEffectSince_bbc08df8", { v0: item.current, v1: item.scheduled ? msg("templateScheduled_c32b1fcf", { v0: item.scheduled }) : "" })
							: item?.scheduled
								? msg("templateScheduledNoneInEffectYet_5d8dfad1", { v0: plural(item.scheduled, "configuration") })
								: msg("notConfigured"),
			};
		}),
		...(openBanking
			? [{ key: "beneficiary", title: msg("beneficiary"), tab: TAB.BENEFICIARY, ok: beneficiaries.length > 0, text: beneficiaries.length ? beneficiaries.map(item => item.name ?? `#${item.id}`).join(", ") : msg("noBeneficiaryLinked") }]
			: []),
	];
	const missing = checks.filter(check => check.ok === false).length + (channel.integration ? 0 : 1);

	return (
		<>
			{channel.linked && <MerchantSetup channel={channel} />}
			<Section
				title={msg("setup_7013af4c")}
				action={fees && (missing ? <WarningText>{plural(missing, "item")}{msg("toFinish")}</WarningText> : <Pill tone="positive" dot>{msg("readyToProcess")}</Pill>)}
			/>
			<Card>
				<SetupBox>
					<ItemList>
						<IntegrationRow channel={channel} readonly={!!channel.linked} />
						{checks.map(check => (
							<Item key={check.key} onOpen={check.tab !== undefined ? () => onOpenTab(check.tab!) : undefined} label={check.title}>
								<CheckMark ok={check.ok} />
								<div className="item-main">
									<div className="item-title">{check.title}</div>
									<div className={`item-sub${check.ok === false ? " missing" : ""}`}>{check.text}</div>
								</div>
								{check.tab !== undefined && <span className={`go${check.ok === false ? " setup" : ""}`}>{check.ok === false ? msg("setUp") : msg("view")}</span>}
								<RowChevron />
							</Item>
						))}
					</ItemList>
				</SetupBox>
			</Card>

			<Section title={msg("details")} />
			<Card>
				<div className="card-body">
					<Facts>
						<dt>{msg("provider")}</dt>
						<dd>{channel.provider?.name ?? <Muted>—</Muted>}</dd>
						<dt>{msg("paymentMethod")}</dt>
						<dd>
							<MethodInline channel={channel} />
							<Muted>{methodTypeLabel(channel.terminal?.type)}</Muted>
						</dd>
						<dt>{msg("currency")}</dt>
						<dd>{channel.currency ? <Chip>{channel.currency}</Chip> : <Muted>—</Muted>}</dd>
						<dt>{msg("cardSchemes")}</dt>
						<dd>{channel.instrument_type && channel.instrument_type !== "unset" ? <CardBrandIcons instrument={channel.instrument_type} /> : <Muted>{msg("notRestricted")}</Muted>}</dd>
						<dt>{msg("status")}</dt>
						<dd>
							<StatusPill status={channelStatus(channel)} />
						</dd>
						<dt>{msg("channelId_8edd65cc")}</dt>
						<dd>
							<Mono>{channel.id}</Mono>
							<CopyButton value={String(channel.id)} label={msg("copyChannelId")} />
						</dd>
						{channel.linked && (
							<>
								<dt>{msg("merchantLink")}</dt>
								<dd>
									<Mono>#{channel.linked.id}</Mono>
									<Muted>{msg("merchant_bae582b3")}{channel.linked.merchant_id}</Muted>
									{channel.linked.order !== undefined && <Muted>{msg("priority")}{channel.linked.order}</Muted>}
								</dd>
							</>
						)}
					</Facts>
				</div>
			</Card>
		</>
	);
};

const IntegrationRow = ({ channel, readonly }: { channel: TPaymentChannel; readonly?: boolean }) => {
	useLocale();
	const integration = channel.integration;
	const refresh = async () => {
		S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
	};
	const connect = () => S_INTEGRATION_SELECT_POPUP.invoke({ channel_id: channel.id, current_id: integration?.id, channel_name: channel.name, method_type: channel.terminal?.type, onSuccess: refresh });
	const disconnect = () => {
		if (!integration) return;
		S_CONFIRM.invoke({
			title: msg("disconnectIntegration"),
			message: msg("templateDisconnectFromThisChannelPaymentsThroughTheChannelStopUn_e3ef992c", { v0: integration.name }),
			confirm: "Disconnect",
			onResult: async value => {
				if (value !== "confirm") return;
				const response = await GD.REQ_API.request({ method: "integrations/disconnect", data: { id: integration.id, channel_id: channel.id } });
				if (response.error) {
					notify.error(response.error.message);
					return;
				}
				notify.success(msg("integrationDisconnected"));
				await refresh();
			},
		});
	};
	const host = integrationHost(integration);
	const method = integrationMethodType(integration);

	return (
		<Item label={msg("integration")}>
			<CheckMark ok={!!integration} />
			<div className="item-main">
				<div className="item-title">{msg("integration")}</div>
				{integration ? (
					<div className="item-sub">
						<span style={{ color: "#344054", fontWeight: 500 }}>{integration.name}</span>
						<span>· {integration.type}</span>
						{method && <span>· {methodTypeLabel(method)}</span>}
						{host && (
							<span>
								· <Mono>{host}</Mono>
							</span>
						)}
						{integration.status !== "active" && <StatusPill status={integration.status} />}
					</div>
				) : (
					<div className="item-sub missing">
						{readonly ? msg("theProviderChannelHasNoIntegrationSoPaymentsCannotBeProcessed") : msg("notConnectedPaymentsCannotBeProcessedYet")}
					</div>
				)}
			</div>
			<span className="item-side">
				{integration && (
					<AdminButton variant="secondarySmall" size="small" onClick={() => S_INTEGRATION_EDIT_POPUP.invoke({ integration_id: integration.id, onSuccess: refresh })}>{msg("open")}</AdminButton>
				)}
				{!readonly &&
					(integration ? (
						<>
							<AdminButton variant="secondarySmall" size="small" onClick={connect}>
								<RefreshCw size={14} />{msg("change")}</AdminButton>
							<AdminButton variant="secondarySmall" size="small" onClick={disconnect} title={msg("disconnectIntegration")} aria-label={msg("disconnectIntegration")}>
								<Unlink size={14} />
							</AdminButton>
						</>
					) : (
						<AdminButton variant="primarySmall" size="small" onClick={connect}>
							<Link2 size={14} />{msg("connectIntegration")}</AdminButton>
					))}
			</span>
		</Item>
	);
};

/* ---------- Merchant link ---------- */

const MerchantSetup = ({ channel }: { channel: TPaymentChannel }) => {
	useLocale();
	const [error, setError] = useState<IError | null>(null);
	const deleted = channel.linked.status === "deleted";

	const toggleLink = async () => {
		setError(null);
		if (deleted) {
			const response = await GD.REQ_API.request({ method: "payments/channel/linked/restore", data: { linked_id: channel.linked.id } });
			if (!response.error) {
				notify.success(msg("channelRestoredForTheMerchant"));
				S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
			} else setError(response.error);
			return;
		}
		S_CONFIRM.invoke({
			title: msg("unlinkChannel"),
			message: msg("unlinkThisChannelFromTheMerchantTheMerchantStopsRoutingPayme_0de1e80f"),
			confirm: msg("unlink"),
			onResult: async (value: "confirm" | "reject"): Promise<void> => {
				if (value !== "confirm") return;
				const response = await GD.REQ_API.request({ method: "payments/channel/linked/delete", data: { linked_id: channel.linked.id } });
				if (!response.error) {
					notify.success(msg("channelUnlinked"));
					S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
				} else setError(response.error);
			},
		});
	};

	return (
		<>
			<Section title={msg("merchantSetup")} />
			{deleted && <Notice tone="warning">{msg("thisLinkIsDeletedTheMerchantNoLongerRoutesPaymentsThroughThe_f250af22")}</Notice>}
			{error && <Notice tone="error">{error.message}</Notice>}
			<Card>
				<div className="card-body">
					<FormParser
						appearance="modern"
						sx={embeddedFormSx}
						size="small"
						grow
						secondaryButton={null}
						buttons={[{ title: deleted ? msg("restoreLink") : msg("unlink"), variant: "secondarySmall", sx: deleted ? undefined : { color: "#b42318", borderColor: "#fecdca" }, onClick: () => void toggleLink() }]}
						onRequest={async () => ({
							setup: {
								alias: channel.linked.alias ?? "",
								mid: channel.linked.processing_mid ?? "",
							},
							$__setup_decorator: {
								blocks: [{ blockless: true, order: ["alias", "mid"], gridTemplateColumns: "minmax(200px, .7fr) minmax(150px, .3fr)" }],
							},
						})}
						onSubmit={async data => {
							const result = await editMerchantChannel({ linked_id: channel.linked.id, order: channel.linked.order, alias: data.setup.alias, mid: data.setup.mid });
							if (result.error) return result;
							notify.success(msg("merchantSetupSaved"));
							S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
							return data;
						}}
					/>
				</div>
			</Card>
		</>
	);
};

/* ---------- Beneficiary (open banking) ---------- */

const BeneficiaryRow = ({ beneficiary, action }: { beneficiary: TBeneficiary; action?: ReactNode }) => { useLocale(); return ((
	<Item>
		<IconTile>
			<Landmark />
		</IconTile>
		<div className="item-main">
			<div style={{ color: "#182230", fontWeight: 600 }}>{beneficiary.name ?? msg("beneficiary_c2e6a9cc") + beneficiary.id}</div>
			<div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2px 12px", color: "#667085", fontSize: 12 }}>
				{beneficiary.iban && (
					<span>
						<Mono>{formatIban(beneficiary.iban)}</Mono> <CopyButton value={beneficiary.iban} label={msg("copyIban")} />
					</span>
				)}
				{beneficiary.bic && <span>BIC <Mono>{beneficiary.bic}</Mono></span>}
				{beneficiary.bank && <span>{beneficiary.bank}</span>}
			</div>
		</div>
		<span className="item-side">{action}</span>
	</Item>
)); };

const ChannelBeneficiarySetup = ({ channel }: { channel: TPaymentChannel }) => {
	useLocale();
	const [error, setError] = useState<IError | null>(null);
	const [busy, setBusy] = useState(false);
	const linkedBeneficiaries = getChannelBeneficiaries(channel);
	const refreshChannel = () => S_CHANNELS_LIST_REQUEST_UPDATE.invoke();
	const choose = () => S_BENEFICIARY_SELECT_POPUP.invoke({ channel, onSuccess: refreshChannel });

	const unlinkBeneficiary = (beneficiary: TBeneficiary) =>
		S_CONFIRM.invoke({
			title: msg("unlinkBeneficiary"),
			message: msg("templateUnlinkFromThisChannelOpenBankingPaymentsNeedABeneficiary_e517666e", { v0: beneficiary.name ?? beneficiary.id }),
			confirm: msg("unlink"),
			onResult: async (value: "confirm" | "reject") => {
				if (value !== "confirm") return;
				setBusy(true);
				setError(null);
				const response = await GD.REQ_API.request({ method: "ob/beneficiary/unlink", data: getBeneficiaryLinkData(channel, beneficiary.id) });
				setBusy(false);
				if (response.error) {
					setError(response.error);
					return;
				}
				notify.success(msg("beneficiaryUnlinked"));
				refreshChannel();
			},
		});

	return (
		<>
			<Section
				title={msg("linkedBeneficiary")}
				action={
					linkedBeneficiaries.length > 0 && (
						<AdminButton variant="secondarySmall" size="small" disabled={busy} onClick={choose}>
							<RefreshCw size={14} />{msg("changeBeneficiary")}</AdminButton>
					)
				}
			/>
			{error && <Notice tone="error">{error.message}</Notice>}
			<Card>
				{linkedBeneficiaries.length === 0 ? (
					<EmptyState
						icon={<Landmark size={20} />}
						title={msg("noBeneficiaryLinked")}
						text={msg("openBankingPaymentsArePaidOutToTheBeneficiaryAccountLinkedHere")}
						action={
							<AdminButton variant="primarySmall" size="small" disabled={busy} onClick={choose}>
								<Link2 size={14} />{msg("linkBeneficiary")}</AdminButton>
						}
					/>
				) : (
					<ItemList>
						{linkedBeneficiaries.map(beneficiary => (
							<BeneficiaryRow
								key={beneficiary.id}
								beneficiary={beneficiary}
								action={
									<AdminButton variant="secondarySmall" size="small" disabled={busy} onClick={() => unlinkBeneficiary(beneficiary)}>
										<Unlink size={14} />{msg("unlink")}</AdminButton>
								}
							/>
						))}
					</ItemList>
				)}
			</Card>
			<SelectBeneficiaryPopup />
		</>
	);
};

const S_BENEFICIARY_SELECT_POPUP = new Signal<{
	channel: TPaymentChannel;
	onSuccess?: () => void | Promise<void>;
} | null>("S_BENEFICIARY_SELECT_POPUP");

const SelectBeneficiaryPopup = () => {
	 useLocale();
	const request = S_BENEFICIARY_SELECT_POPUP.use();
	const [error, setError] = useState<IError | null>(null);
	const [beneficiaries, setBeneficiaries] = useState<TBeneficiary[] | null>(null);
	const [busy, setBusy] = useState(false);
	const [query, setQuery] = useState("");

	const close = () => {
		setError(null);
		setQuery("");
		S_BENEFICIARY_SELECT_POPUP.invoke(null);
	};

	useEffect(() => {
		if (!request) return;
		let active = true;
		setBusy(true);
		setError(null);
		void GD.REQ_API.request({ method: "ob/beneficiary/list", data: {} }).then(response => {
			if (!active) return;
			setBusy(false);
			if (response.error) {
				setError(response.error);
				setBeneficiaries([]);
				return;
			}
			const responseData = response.data as any;
			const list = Array.isArray(responseData) ? responseData : Array.isArray(responseData?.data) ? responseData.data : [];
			setBeneficiaries(list.map(normalizeBeneficiary).filter((beneficiary: TBeneficiary) => beneficiary.status !== "deleted"));
		});
		return () => {
			active = false;
		};
	}, [request?.channel.id, request?.channel.linked?.id]);

	const visible = useMemo(
		() => (beneficiaries ?? []).filter(beneficiary => matchesQuery(query, [beneficiary.name, beneficiary.iban, beneficiary.bic, beneficiary.bank, beneficiary.id])),
		[beneficiaries, query],
	);

	if (!request) return null;

	const linkedIds = new Set(getChannelBeneficiaries(request.channel).map(beneficiary => beneficiary.id));

	const linkBeneficiary = async (beneficiary: TBeneficiary) => {
		setBusy(true);
		setError(null);
		const response = await GD.REQ_API.request({ method: "ob/beneficiary/link", data: getBeneficiaryLinkData(request.channel, beneficiary.id) });
		setBusy(false);
		if (response.error) {
			setError(response.error);
			return;
		}
		notify.success(msg("beneficiaryLinked"));
		await request.onSuccess?.();
		close();
	};

	return (
		<ProviderDialog width={760} eyebrow={msg("templateChannel_58a1744e", { v0: request.channel.name })} title={msg("linkABeneficiary")} onClose={close}>
			<DialogBody>
				{error && <Notice tone="error">{error.message}</Notice>}
				<div style={{ display: "flex", marginBottom: 12 }}>
					<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameIbanBicOrBank")} />
				</div>
				<Card>
					{beneficiaries === null ? (
						<SkeletonRows rows={3} />
					) : visible.length === 0 ? (
						<EmptyState
							icon={<Landmark size={20} />}
							title={beneficiaries.length ? msg("nothingMatchesTheSearch") : msg("noBeneficiariesYet")}
							text={beneficiaries.length ? msg("tryAShorterQuery") : msg("createABeneficiaryInProvidersBeneficiariesFirst")}
						/>
					) : (
						<ItemList>
							{visible.map(beneficiary => (
								<BeneficiaryRow
									key={beneficiary.id}
									beneficiary={beneficiary}
									action={
										linkedIds.has(beneficiary.id) ? (
											<Pill tone="positive" dot>{msg("linked")}</Pill>
										) : (
											<AdminButton variant="primarySmall" size="small" disabled={busy} onClick={() => void linkBeneficiary(beneficiary)}>
												<Plus size={14} />{msg("link")}</AdminButton>
										)
									}
								/>
							))}
						</ItemList>
					)}
				</Card>
			</DialogBody>
		</ProviderDialog>
	);
};

/* ---------- Actual fees (merchant link) ---------- */

const ACTUAL_SECTIONS: Record<string, { title: string; hint: string }> = {
	channel: { get title() { return msg("channel"); }, get hint() { return msg("ratesLimitsAndSettingsOfThisMerchantChannel"); } },
	client: { get title() { return msg("client"); }, get hint() { return msg("clientWideRollingReserveAndSetupFees"); } },
	integration: { get title() { return msg("integration"); }, get hint() { return msg("processingAccountOfTheProviderChannel"); } },
};

const isActivationBlock = (value: unknown): value is { activated_from?: unknown; data?: unknown } =>
	!!value && typeof value === "object" && !Array.isArray(value) && ("activated_from" in value || "data" in value);

const ActualSection = ({ name, value }: { name: string; value: unknown }) => {
	useLocale();
	const meta = ACTUAL_SECTIONS[name] ?? { title: name.replace(/_/g, " "), hint: "" };
	const record = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
	const blocks = record ? Object.entries(record).filter(([, item]) => isActivationBlock(item)) : [];
	const plain = record ? Object.fromEntries(Object.entries(record).filter(([, item]) => !isActivationBlock(item))) : {};
	const empty = !record || Object.keys(record).length === 0;

	return (
		<>
			<Section title={meta.title} action={meta.hint && <span style={{ color: "#98a2b3" }}>{meta.hint}</span>} />
			{empty ? (
				<Card>
					<div className="card-body">
						<Muted>{msg("notConfigured")}</Muted>
					</div>
				</Card>
			) : (
				<>
					{blocks.map(([type, block]) => {
						const activation = block as { activated_from?: unknown; data?: unknown };
						const date = toDate(activation.activated_from);
						return (
							<Card key={type}>
								<div className="card-head">
									<div className="card-title">{type.charAt(0).toUpperCase() + type.slice(1)}</div>
									{date && <Pill tone="positive" dot>{msg("templateInEffectSince_dbdd63f9", { v0: formatDate(date) })}</Pill>}
								</div>
								<div className="card-body">
									{activation.data && typeof activation.data === "object" ? <ValueTree value={activation.data as Record<string, unknown>} /> : <Muted>{msg("noValues")}</Muted>}
								</div>
							</Card>
						);
					})}
					{Object.keys(plain).length > 0 && (
						<Card>
							<div className="card-body">
								<ValueTree value={plain} />
							</div>
						</Card>
					)}
				</>
			)}
		</>
	);
};

const RawToggle = styled.details`
	margin-top: 24px;
	summary {
		width: fit-content;
		color: #175cd3;
		font-size: 12px;
		font-weight: 500;
		cursor: pointer;
	}
	> div {
		margin-top: 10px;
	}
`;

const ActualFeesView = ({ data }: { data?: TPaymentChannel["actual_fees"] }) => {
	useLocale();
	if (!data || typeof data !== "object")
		return (
			<Card>
				<EmptyState icon={<Store size={20} />} title={msg("noActualFeesYet")} text={msg("theServerDidNotReturnTheFeesInEffectForThisMerchantChannel")} />
			</Card>
		);
	const record = data as Record<string, unknown>;
	const order = ["channel", "client", "integration"];
	const keys = [...order.filter(key => key in record), ...Object.keys(record).filter(key => !order.includes(key) && !["merchant_id", "merchant_channel_id"].includes(key))];
	return (
		<>
			<Notice tone="info">{msg("whatAppliesToThisMerchantChannelTodayCombinedFromTheChannelT_59d12b5f")}</Notice>
			{keys.map(key => (
				<ActualSection key={key} name={key} value={record[key]} />
			))}
			<RawToggle>
				<summary>{msg("showRawData")}</summary>
				<div>
					<Pre data={maskSecrets(data)} />
				</div>
			</RawToggle>
		</>
	);
};
